<template>
    <div v-if="dashboard && Object.keys(dashboard).length">
        <el-tooltip effect="dark"
                    :content="$t('dashboard.copyDashboard')"
                    :open-delay="openDelay"
                    placement="top">
            <base-button outline
                         fixed-width="w-37"
                         @click="showDialog = true">
                <div class="flex items-center">
                    <CopyIcon class="w-4-5 h-4-5"/>
                    <span class="mx-1 text-base font-bold">{{ $t('general.copy') }}</span>
                </div>
            </base-button>
        </el-tooltip>
        <Modal :visible.sync="showDialog"
               :append-to-body="true">
            <template v-slot:title>
                <h3 class="text-main-2xl font-semibold main-text-color">
                    {{ $t('dashboard.copyDashboard') }}
                </h3>
            </template>
            <div class="py-5">
                <p class="mb-2 main-text-color">
                    {{ $t('general.account') }}
                    ({{ $t('dashboard.copyDashboardIntoSelectedAccount') }})
                </p>
                <base-select
                    :data="allAccounts"
                    labelKey="name"
                    valueKey="ID"
                    :multiple="false"
                    class="w-full mb-2"
                    v-model="account"/>
                <p class="mb-2 main-text-color">
                    {{ $t('dashboard.newDashboardName') }}
                </p>
                <el-input v-model="dashboardCopy.DashboardTitle"/>
                <el-collapse
                    v-model="activeCollapse"
                    class="mt-3">
                    <el-collapse-item v-for="group in widgetGroups"
                        class="my-2"
                        :title="groupTitle(group)"
                        :key="group.WidgetGroupID"
                        :name="group.WidgetGroupID"
                    >
                        <template slot="title">
                            <el-checkbox :value="group.isSelected"
                                         @change.native.stop="toggleGroupSelection(group)"/>
                            <span class="mx-2 main-text-color">
                                {{ group.WidgetGroupTitle || $t('dashboard.groupID') + ': ' + group.WidgetGroupID }}
                            </span>
                        </template>
                        <template v-if="displayWidgetList">
                            <div
                                v-for="widget in group.WidgetList"
                                :key="widget.WidgetID"
                                class="w-full flex items-center">
                                <el-checkbox
                                    :value="widget.isSelected"
                                    @change="toggleWidgetSelection(widget, group)"
                                />
                                <component class="text-primary my-1 mx-2"
                                        :is="getWidgetIcon(widget)"/>
                                <span class="flex-1 truncate main-text-color">
                                    {{ widget.Title }}
                                </span>
                            </div>
                        </template>
                    </el-collapse-item>
                </el-collapse>
            </div>
            <template v-slot:footer>
                <div class="border-t-2 border-gray-300 py-4 flex items-center justify-between px-6 main-bg-light">
                    <cancel-button
                        class="md:mx-4"
                        @on-click="onCancel"
                    />
                    <confirm-button
                        :loading="loading"
                        @on-click="onCopy"
                    />
                </div>
            </template>
        </Modal>
    </div>
</template>
<script>
    import cloneDeep from 'lodash/cloneDeep'
    import { CopyIcon } from 'vue-feather-icons'
    import { templateIcons } from '@/enum/widgetDataTypes'
    import { Collapse, CollapseItem, Checkbox } from 'element-ui'
    import { DashboardApi } from '@/api/dashboardApi'
    import CancelButton from "@/components/Common/Buttons/CancelButton"
    import ConfirmButton from "@/components/Common/Buttons/ConfirmButton"
    import { Notification } from 'element-ui'

    export default {
        components: {
            Modal: () => import('@/components/Common/Modal'),
            CopyIcon,
            [Checkbox.name]: Checkbox,
            [Collapse.name]: Collapse,
            [CollapseItem.name]: CollapseItem,
            CancelButton,
            ConfirmButton,
        },
        props: {
            dashboard: {
                type: Object,
                default: () => ({})
            }
        },
        data() {
            return {
                activeCollapse: [],
                loading: false,
                openDelay: 200,
                showDialog: false,
                account: null,
                displayWidgetList: true,
                dashboardCopy: {
                    ...cloneDeep(this.dashboard),
                    DashboardTitle: `${this.dashboard.DashboardTitle} - Copy`
                }
            }
        },
        computed: {
            widgetGroups() {
                return this.dashboardCopy.WidgetGroupList || []
            },
            allAccounts() {
                return this.$store.getters['entities/accountsList']
            },
            currentAccountId() {
                return this.$store.state.entities.selectedAccountID
            }
        },
        methods: {
            groupTitle(group) {
                return group.WidgetGroupTitle ? this.$t(group.WidgetGroupTitle) : `${this.$t('general.group')} #${group.WidgetGroupID}`
            },
            getWidgetIcon(widget) {
                const { DataTypeID } = widget.WidgetLayout
                return templateIcons[DataTypeID]
            },
            toggleGroupSelection(group) {
                this.displayWidgetList = false
                group.isSelected = !group.isSelected

                group.WidgetList.forEach(widget => {
                    widget.isSelected = group.isSelected
                })

                this.$nextTick(() => {
                    this.displayWidgetList = true
                })
            },
            toggleWidgetSelection(widget, group) {
                this.displayWidgetList = false
                widget.isSelected = !widget.isSelected

                if(widget.isSelected && !group.isSelected) {
                    group.isSelected = true
                }

                const isFullyUnchecked = !group.WidgetList.some(el => el.isSelected)
                if (isFullyUnchecked) {
                    group.isSelected = false
                }

                this.$nextTick(() => {
                    this.displayWidgetList = true
                })
            },
            async onCopy() {
                let widgetGroupList  = []
                if (this.dashboardCopy.WidgetGroupList.length) {
                    widgetGroupList = this.dashboardCopy.WidgetGroupList
                    .map(widgetGroup => {
                        if (widgetGroup.isSelected) {
                            const newWidgetGroupItem = {}
                            newWidgetGroupItem.WidgetGroupID = widgetGroup.WidgetGroupID
                            newWidgetGroupItem.WidgetList = widgetGroup.WidgetList
                                .filter(el => el.isSelected)
                                .map(el => el.WidgetID) || []
                            return newWidgetGroupItem
                        }
                    })
                    .filter(el => el)
                }

                try {
                    this.loading = true
                    const copiedDashboard = {
                        AccountID: this.account,
                        DashboardID: this.dashboardCopy.DashboardID,
                        DashboardCopyTitle: this.dashboardCopy.DashboardTitle,
                        WidgetGroupCopyList: widgetGroupList
                    }

                    await DashboardApi.copy(copiedDashboard)
                    Notification.success(this.$t('general.notification.success'))
                } catch (e) {
                    console.warn(e)
                } finally {
                    this.loading = false
                    this.showDialog = false
                }
            },
            fillAccount() {
                this.account = this.currentAccountId
            },
            onCancel () {
                this.showDialog = false
            }
        },
        mounted() {
            this.widgetGroups.map((group) => {
                group['isSelected'] = true
                group.WidgetList.map(widget => {
                    widget['isSelected'] = true
                    return widget
                })
                return group
            })
            this.fillAccount()
        }
    }
</script>

<style lang="scss" scoped>
::v-deep .el-input__inner {
    background: var(--mainBgColor-color);
    color: var(--mainTextColor-color);
}
::v-deep .el-collapse-item__header {
    border-color: var(--mainBgColor-color);
}
::v-deep .el-icon-arrow-right {
    color: var(--mainTextColor-color);
}
</style>
